import React, { useMemo, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import formatNumber from '../../../../utils/formatNumber.js';
import getApiError from '../../../../utils/getApiError.js';
import Api from '../../../../api/global.service';

const LoteDividirEntregaModal = forwardRef(({
    onSuccess,
    lote = {},
    entregas = {},
    coopConfig = {},
},ref ) => {
    const [show, setShow] = useState(false);
    const [pesoAsignar, setPesoAsignar] = useState(0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const entrega = Array.isArray(entregas) ? entregas[0] : entregas;

    // Valores calculados
    let kgsTotales = lote?.lote_kgs_totales || 0;
    let kgsPorAsignar = lote?.lote_kgs_por_entregar || 0;

    let pesoNetoEntrega = entrega?.peso_neto || 0;
    let pesoDisponible = entrega?.peso_pendiente !== undefined ? entrega?.peso_pendiente : pesoNetoEntrega;
    let unidades = coopConfig.acopioUnits() ? coopConfig.acopioUnits()?.abreviacion : '';
    if (coopConfig.hasAcopioUnitsExtra()) {
        unidades = coopConfig.acopioUnitsExtra()?.abreviacion;
        kgsTotales = lote?.lote_kgs_totales_extra || 0;
        kgsPorAsignar = lote?.lote_kgs_por_entregar_extra || 0;
        pesoNetoEntrega = entrega?.peso_neto_extra || 0;
        pesoDisponible = entrega?.peso_pendiente_extra !== undefined ? entrega?.peso_pendiente_extra : pesoNetoEntrega;
    }

    //To call outside
    useImperativeHandle(ref, () => ({
        show: showHandler,
    }));

    useEffect(() => {
        if (show) {
            setPesoAsignar(0);
            setError('');
        }
    }, [show]);

    const showHandler = () => {
        setShow(true);
    };

    const onCloseHandler = () => {
        setShow(false);
    };

    const handleSave = async () => {
        let peso = parseFloat(pesoAsignar);
        
        if (isNaN(peso) || peso <= 0) {
            setError('Ingresa un peso válido');
            return;
        }

        if (peso > pesoDisponible) {
            setError('El peso debe ser menor o igual al peso disponible');
            return;
        }

        setError('');
        setLoading(true);

        if (coopConfig.hasAcopioUnitsExtra()) {
            peso = peso / (coopConfig.acopioUnitsConversion()?.conversion || 1);
        } 

        try {
            const payload = {
                id_acopio: entrega.id_acopio,
                id_lote: lote.id,
                peso: peso
            };

            await Api.create('acopios/asignacion', payload);
            onSuccess(peso);
            onCloseHandler();
        } catch (error) {
            console.error("Error asignando peso:", error);
            setError(getApiError(error, 'Ocurrió un error al asignar el peso. Por favor, inténtalo de nuevo.'));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setPesoAsignar(e.target.value);
        setError('');
    };

    const isWarning = useMemo(() => {
        return parseFloat(pesoAsignar) > kgsPorAsignar;
    }, [pesoAsignar, kgsPorAsignar]);
    
    if (!show) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100 }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: '450px' }}>
                <div className="modal-content" style={{ borderRadius: '20px', border: 'none', backgroundColor: '#F5F2F5', boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}>

                    {/* Header */}
                    <div className="modal-header" style={{ backgroundColor: '#f1eaea' }}>
                        <h5 className="modal-title w-100" style={{ fontWeight: '400', fontSize: '1rem', color: '#555' }}>
                            Asigna parte de la entrega al lote <span style={{ fontWeight: 'bold', color: '#333', fontSize: '1rem' }}>{lote.nombre || ''}</span>
                        </h5>
                        <button
                            type="button"
                            className="close p-0 m-0"
                            onClick={onCloseHandler}
                            aria-label="Close"
                            style={{ outline: 'none', opacity: 0.5, fontSize: '2rem', fontWeight: '300' }}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="modal-body" style={{ backgroundColor: '#f5f1f2', paddingLeft: '30px', paddingRight: '30px' }}>
                        {/* Info Rows */}
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-secondary" style={{ fontSize: '1rem' }}>Volumen total:</span>
                            <span className="font-weight-bold text-dark" style={{ fontSize: '1rem' }}>{formatNumber(kgsTotales)} {unidades}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                            <span className="text-secondary" style={{ fontSize: '1rem' }}>Volumen por asignar:</span>
                            <span className="font-weight-bold" style={{ fontSize: '1rem', color: '#789D16' }}>{formatNumber(kgsPorAsignar)} {unidades}</span>
                        </div>

                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-secondary" style={{ fontSize: '1rem' }}>Peso neto entrega:</span>
                            <span className="font-weight-bold text-dark" style={{ fontSize: '1rem' }}>{formatNumber(pesoNetoEntrega)} {unidades}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-4">
                            <span className="text-secondary" style={{ fontSize: '1rem' }}>Peso disponible:</span>
                            <span className="font-weight-bold" style={{ fontSize: '1rem', color: '#789D16' }}>{formatNumber(pesoDisponible)} {unidades}</span>
                        </div>

                        {/* Input Section */}
                        <div className="form-group mb-2">
                            <label className="font-weight-bold text-dark mb-2" style={{ fontSize: '1rem' }}>Asignar peso al lote:</label>
                            <div className="input-group input-group-lg" style={{ width: '50%' }}>
                                <input
                                    type="number"
                                    className="form-control border-right-0"
                                    value={pesoAsignar}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                    style={{
                                        borderRadius: '10px 0 0 10px',
                                        height: '45px',
                                        fontSize: '1rem',
                                        borderColor: '#ced4da',
                                        backgroundColor: '#fff',
                                        paddingLeft: '10px',
                                    }}
                                />
                                <div className="input-group-append">
                                    <span className="input-group-text border-left-0 text-muted" style={{ borderRadius: '0 10px 10px 0', borderColor: '#ced4da', fontSize: '1rem', backgroundColor: '#f5f1f2' }}>{unidades}</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Messages */}
                        <div style={{ minHeight: '20px' }}>
                            {/* Error Message */}
                            {error && (
                                <div style={{ marginBottom: '10px' }}>
                                    <span className="text-danger align-items-center" style={{ fontSize: '0.75rem' }}>
                                        {error}
                                    </span>
                                </div>
                            )}
                            {/* Warning Message */}
                            {isWarning && (
                                <div style={{ marginBottom: '10px' }}>
                                    <span className="text-warning align-items-center" style={{ fontSize: '0.75rem' }}>
                                        El peso por asignar es mayor a los Kgs por asignar del lote
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer" style={{ backgroundColor: '#f9f2f5' }}>
                        <button
                            type="button"
                            className="btn btn-sirio"
                            style={{
                                fontWeight: '600',
                                opacity: loading ? 0.7 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                            onClick={handleSave}
                            disabled={loading}
                        >
                        <span style={{ fontSize: '1rem', fontWeight: '600' }}>
                            {loading ? 'Guardando...' : 'Guardar'}
                        </span>
                        </button>

                        <button
                            className="btn float-right"
                            style={{
                                backgroundColor: '#e9e4e4',
                                opacity: loading ? 0.7 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                            type="button"
                            onClick={onCloseHandler}
                            disabled={loading}
                        >
                        <span style={{ fontSize: '1rem', fontWeight: '600' }}>Cancelar</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});


export default LoteDividirEntregaModal;
